import React, { useMemo } from "react";
import { useAuthStore } from "@/store/auth.store";
import { useTenantStore } from "@/store/tenant.store";
import {
  getPageKeyByPath as registryGetPageKeyByPath,
  getActionsForPage,
  isPageLicensed,
} from "@repo/permissions";
import {
  buildTenantPermissionMap,
  getEffectivePermissionsForPage,
  resolveTenantMembership,
} from "@/lib/tenant-role-permissions.js";

export { registryGetPageKeyByPath as getPageKeyByPath };

export function usePermission() {
  const user = useAuthStore((state) => state.user);
  const selectedTenantId = useTenantStore((state) => state.selectedTenantId);

  const membership = useMemo(
    () => resolveTenantMembership(user, selectedTenantId),
    [user, selectedTenantId],
  );

  const licensedModules = useMemo(
    () => membership?.tenant?.settings?.modules || [],
    [membership],
  );

  const permissionMap = useMemo(
    () => buildTenantPermissionMap(membership, user),
    [membership, user],
  );

  const getEffectivePermissions = React.useCallback(
    (pageKey) => getEffectivePermissionsForPage(permissionMap, pageKey),
    [permissionMap],
  );

  const can = React.useCallback(
    (pageKey, action) => {
      if (!pageKey || !action) return false;
      if (!isPageLicensed(pageKey, licensedModules)) return false;
      const perms = getEffectivePermissionsForPage(permissionMap, pageKey);
      if (!perms || perms.length === 0) return false;
      return perms.includes(action);
    },
    [permissionMap, licensedModules],
  );

  const canView = React.useCallback((pageKey) => can(pageKey, "View"), [can]);

  const hasAny = React.useCallback(
    (pageKey) => {
      if (!pageKey) return false;
      if (!isPageLicensed(pageKey, licensedModules)) return false;
      const perms = getEffectivePermissionsForPage(permissionMap, pageKey);
      return Array.isArray(perms) && perms.length > 0;
    },
    [permissionMap, licensedModules],
  );

  const canExport = React.useCallback((pageKey) => can(pageKey, "Export"), [can]);
  const canApprove = React.useCallback((pageKey) => can(pageKey, "Approve"), [can]);
  const canAssign = React.useCallback((pageKey) => can(pageKey, "Assign"), [can]);

  const getAvailableActionsForPage = React.useCallback((pageKey) => getActionsForPage(pageKey), []);

  return React.useMemo(
    () => ({
      can,
      canView,
      canExport,
      canApprove,
      canAssign,
      hasAny,
      getAvailableActionsForPage,
      permissionMap,
      licensedModules,
      tenantFeatures: membership?.tenant?.settings?.features || {},
    }),
    [
      can,
      canView,
      canExport,
      canApprove,
      canAssign,
      hasAny,
      getAvailableActionsForPage,
      permissionMap,
      licensedModules,
      membership,
    ],
  );
}
